import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Feature,
  getFeatureByAddOn,
  getFeatureFromPlan,
  getIntervalPathParam,
  getQuotaPathParam,
  getRecurlySubUuIdPathParam,
  OrderPreviewRequest,
  PlanInterval,
  ProductPreviewRequest,
  SubscriptionSource,
  toRecurlyAddOnCode,
} from '../models';
import { fromOrder, fromSubscription, useAppDispatch } from '../store';
import { useNavigate, useQuery } from '../routes';
import { plan } from '../layout/routes';
import {
  PLAN_BILLING_QUERY_PARAM,
  PLAN_FEATURE_QUERY_PARAM,
  PLAN_QUANTITY_QUERY_PARAM,
  PRODUCT_QUERY_PARAMS,
} from '../utils/Constants';

const TRUE_PREVIEW_QUERY_FEATURES = [
  Feature.TRUE_BUNDLE_MANUAL,
  Feature.TRUE_BUNDLE_AUTOMATION,
  Feature.TRUE_AUTOMATION,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_TEAM,
  Feature.PER_USER_KSE,
  Feature.UNLIMITED_ENGINE,
  Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
  Feature.TESTCLOUD_SESSION_MOBILE_APP,
  Feature.TESTCLOUD_SESSION_LIVE_TESTING,
  Feature.TESTOPS_G3,
];

type AbortablePreviewDispatch = {
  abort(): void;
};

export const usePreviewTrue = () => {
  const dispatch = useAppDispatch();
  const { navigate, replaceQuery } = useNavigate();
  const { queryDictionary } = useQuery();
  const selectTrueSubscriptionList = useMemo(
    () => fromSubscription.selectTrueSubscriptionList(SubscriptionSource.RECURLY),
    [],
  );
  const subscriptions = useSelector(
    selectTrueSubscriptionList,
  );
  // Standalone testing products (TestCloud, KSE, KRE) are not True-bundle subscriptions, so they
  // are absent from selectTrueSubscriptionList. Detecting a monthly->annual switch for those SKUs
  // needs the full set of self-serve (Recurly) subscriptions.
  const selectRecurlySubscriptionList = useMemo(
    () => fromSubscription.selectSubscriptionsBySource(SubscriptionSource.RECURLY),
    [],
  );
  const recurlySubscriptions = useSelector(
    selectRecurlySubscriptionList,
  );

  const prevRequestRef = useRef<string | undefined>(undefined);
  const previewDispatchRef = useRef<AbortablePreviewDispatch | undefined>(undefined);
  const orderPreviewRequest = useSelector(fromOrder.selectOrderPreviewRequest);
  const truePlatformPreviewRequest = useSelector(fromOrder.selectTruePlatformPreviewRequest);
  const addOns = useSelector(fromOrder.selectAddOns);
  const couponName = useSelector(fromOrder.selectCouponName);
  const pendingOrder = useSelector(fromOrder.selectPendingOrder);
  const hasPendingOrder = !!pendingOrder && pendingOrder.length > 0;
  const dictionary = queryDictionary();

  useEffect(() => {
    const hasPlanSelectionQuery = [
      PLAN_FEATURE_QUERY_PARAM,
      PLAN_QUANTITY_QUERY_PARAM,
      PLAN_BILLING_QUERY_PARAM,
      ...PRODUCT_QUERY_PARAMS,
      ...TRUE_PREVIEW_QUERY_FEATURES.flatMap(feature => [
        getQuotaPathParam(feature),
        getIntervalPathParam(feature),
        getRecurlySubUuIdPathParam(feature),
      ]),
    ].some(param => dictionary[param] !== undefined);

    const updateDictionary = (
      name: string,
      value?: any,
    ) => {
      if (value === undefined || value === null || value === '') {
        delete dictionary[name];
      } else {
        dictionary[name] = value;
      }
    };

    const isMonthlyToAnnualSwitchProduct = (product: ProductPreviewRequest): boolean => {
      const feature = getFeatureFromPlan(product.productCode);
      return product.quantity === 0
        && !!product.recurlySubscriptionUuid
        && PlanInterval.getIntervalFromPlanCode(product.productCode) === PlanInterval.YEAR
        && recurlySubscriptions.some(sub => sub.feature === feature
          && sub.source === SubscriptionSource.RECURLY
          && sub.billingInterval === PlanInterval.MONTH
          && sub.recurlySubUuId === product.recurlySubscriptionUuid);
    };

    const isSelectedProduct = (product: ProductPreviewRequest): boolean => (
      product.quantity > 0 || isMonthlyToAnnualSwitchProduct(product)
    );

    const updateQueryParams = (
      products: ProductPreviewRequest[],
      addOn: Record<string, number> | undefined,
    ) => {
      // Keep plan query params in sync with the cart so refresh, share URL,
      // and the confirmation page can rebuild the same preview request.
      delete dictionary[PLAN_FEATURE_QUERY_PARAM];
      delete dictionary[PLAN_QUANTITY_QUERY_PARAM];
      delete dictionary[PLAN_BILLING_QUERY_PARAM];
      TRUE_PREVIEW_QUERY_FEATURES.forEach(feature => {
        const prefix = Feature.getFeaturePrefix(feature);
        const product = products.find(
          product => getFeatureFromPlan(product.productCode) === feature
            && isSelectedProduct(product),
        );
        updateDictionary(`${prefix}Quota`, product?.quantity);
        updateDictionary(
          `${prefix}Cycle`,
          product ? PlanInterval.getIntervalFromPlanCode(product.productCode) : undefined,
        );
        updateDictionary(getRecurlySubUuIdPathParam(feature), product?.recurlySubscriptionUuid);
      });
      if (addOn) {
        Object.entries(addOn).forEach(([addOn, session]) => {
          const addOnFeature = getFeatureByAddOn(toRecurlyAddOnCode(addOn));
          const prefix = Feature.getFeaturePrefix(addOnFeature);
          updateDictionary(`${prefix}Quota`, session > 0 ? session : undefined);
        });
      }

      navigate(plan.path, replaceQuery(dictionary));
    };

    const getPreviewProducts = (): ProductPreviewRequest[] => {
      if (orderPreviewRequest?.singleProductList?.length) {
        return orderPreviewRequest.singleProductList;
      }
      return truePlatformPreviewRequest ? [truePlatformPreviewRequest] : [];
    };

    const buildPreviewRequest = (
      products: ProductPreviewRequest[],
      filteredAddOn: Record<string, number> | undefined,
    ): OrderPreviewRequest => {
      const selectedProducts = products.filter(isSelectedProduct);
      const previewProducts = selectedProducts.length > 0 ? selectedProducts : products.slice(0, 1);
      const hasAddOns = filteredAddOn && Object.keys(filteredAddOn).length > 0;
      // A product that carries its OWN add-ons is a True Bundle: the set is fixed by
      // `trueBundleConfig` and the BE rejects anything else (`bundle.addons.mismatch`), so it
      // must survive untouched. The page-level add-on picker (legacy True Team TestCloud
      // add-ons) keeps riding on the first product that has none of its own.
      const globalAddOnTarget = previewProducts.findIndex(product => !product.addOns);
      return {
        singleProductList: previewProducts.map((product, index) => {
          const { addOns, ...productWithoutAddOns } = product;
          if (addOns && Object.keys(addOns).length > 0) {
            return { ...productWithoutAddOns, addOns };
          }
          return {
            ...productWithoutAddOns,
            ...(index === globalAddOnTarget && hasAddOns ? { addOns: filteredAddOn } : {}),
          };
        }),
        ...(couponName ? { couponName } : {}),
      };
    };

    const shouldPreview = (
      previewRequest: OrderPreviewRequest,
      hasSelectedProduct: boolean,
      hasSelectedAddOn: boolean,
    ) => {
      if (previewRequest.singleProductList.length === 0) {
        return false;
      }

      const serializedRequest = JSON.stringify(previewRequest);
      if (prevRequestRef.current === serializedRequest) {
        return false;
      }

      if (subscriptions.length === 0) {
        return hasSelectedProduct;
      }

      return hasSelectedProduct || hasSelectedAddOn;
    };

    // do not preview when an order is pending; reset request/response instead
    if (hasPendingOrder) {
      prevRequestRef.current = undefined;
      if (previewDispatchRef.current) {
        previewDispatchRef.current.abort();
        previewDispatchRef.current = undefined;
      }
      dispatch(fromOrder.resetOrderPreviewRequest());
      dispatch(fromOrder.resetOrderPreviewResponse());
      return undefined;
    }

    const previewProducts = getPreviewProducts();
    const hasPreviewRequestState = !!orderPreviewRequest || !!truePlatformPreviewRequest;
    const filteredAddOn = addOns
      ? Object.fromEntries(Object.entries(addOns)
        .filter(([, session]) => session > 0))
      : {};
    const hasSelectedProduct = previewProducts.some(isSelectedProduct);
    const hasSelectedAddOn = Object.keys(filteredAddOn).length > 0;
    if (!hasSelectedProduct && !hasSelectedAddOn) {
      prevRequestRef.current = undefined;
      if (previewDispatchRef.current) {
        previewDispatchRef.current.abort();
        previewDispatchRef.current = undefined;
      }
      dispatch(fromOrder.resetOrderPreviewResponse());
      if (!hasPreviewRequestState && hasPlanSelectionQuery) {
        return undefined;
      }
      updateQueryParams(previewProducts, addOns);
      return undefined;
    }

    const truePreviewRequest = buildPreviewRequest(previewProducts, filteredAddOn);
    if (shouldPreview(truePreviewRequest, hasSelectedProduct, hasSelectedAddOn)) {
      const serializedRequest = JSON.stringify(truePreviewRequest);
      const previewTimeout = window.setTimeout(() => {
        if (previewDispatchRef.current) {
          prevRequestRef.current = undefined;
          previewDispatchRef.current.abort();
        }
        prevRequestRef.current = serializedRequest;
        const previewDispatch = dispatch(fromOrder.doPreviewOrder(truePreviewRequest));
        previewDispatchRef.current = previewDispatch;
        previewDispatch
          .then(action => {
            if (previewDispatchRef.current === previewDispatch
              && fromOrder.doPreviewOrder.rejected.match(action)) {
              prevRequestRef.current = undefined;
            }
          })
          .finally(() => {
            if (previewDispatchRef.current === previewDispatch) {
              previewDispatchRef.current = undefined;
              updateQueryParams(
                truePreviewRequest.singleProductList,
                addOns,
              );
            }
          });
      }, 350);

      return () => {
        window.clearTimeout(previewTimeout);
        if (previewDispatchRef.current) {
          prevRequestRef.current = undefined;
          previewDispatchRef.current.abort();
          // the aborted dispatch's .finally must not pass the identity check —
          // otherwise it rewrites the URL with the superseded request
          previewDispatchRef.current = undefined;
        }
      };
    }

    return undefined;
  }, [
    orderPreviewRequest,
    truePlatformPreviewRequest,
    addOns,
    couponName,
    hasPendingOrder,
    subscriptions,
    recurlySubscriptions,
  ]);
};
